import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { amountInWords, formatMoney, formatQuantity, toDecimal } from '@erp/shared';
import { actsApi } from '../api/acts';

/** «08 августа 2026 г.» — формат даты в шапке бланка. */
function formatDateLong(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateShort(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Число позиций прописью: «Всего оказано услуг 3, на сумму …». */
function itemsCountWord(count: number): string {
  const lastTwo = count % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'наименований';
  const lastOne = count % 10;
  if (lastOne === 1) return 'наименование';
  if (lastOne >= 2 && lastOne <= 4) return 'наименования';
  return 'наименований';
}

/**
 * Реквизиты стороны одной строкой: наименование, ИНН/КПП, адрес — так их
 * печатает 1С в строках «Исполнитель:» и «Заказчик:».
 */
function partyLine(party: {
  fullName: string;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
}): string {
  const parts: string[] = [party.fullName];
  if (party.inn) parts.push(party.kpp ? `ИНН ${party.inn}, КПП ${party.kpp}` : `ИНН ${party.inn}`);
  if (party.addressFull) parts.push(party.addressFull);
  return parts.join(', ');
}

/**
 * PDF собирается как «скриншот» отрисованного бланка (html2canvas → jsPDF) —
 * тем же способом, что у счёта: печатная форма на экране и в PDF гарантированно
 * совпадают, без второй реализации вёрстки.
 */
async function downloadActPdf(element: HTMLElement, filename: string): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => el.classList.contains('no-print'),
  });

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgHeight = (canvas.height * pageWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/png');

  // Длинный акт разбивается на листы сдвигом одного изображения вверх —
  // jsPDF обрезает лишнее по границам страницы.
  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
}

/**
 * Печатная форма акта выполненных работ (оказанных услуг).
 *
 * Унифицированной формы у акта нет: с 01.01.2013 организация утверждает её
 * сама, обязателен лишь состав реквизитов из ч. 2 ст. 9 Федерального закона
 * № 402-ФЗ. Вёрстка снята с типового бланка, привычного контрагентам:
 * заголовок с жирной чертой, реквизиты сторон, таблица работ, итоги справа
 * под таблицей, сумма прописью и два блока подписей.
 *
 * Отдельный маршрут без AppShell, как у счёта и УПД: бланк должен занимать
 * весь лист. window.print() вызывается автоматически при обычном открытии.
 */
export function ActPrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get('download') === '1';
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const actQuery = useQuery({
    queryKey: ['act', id],
    queryFn: () => actsApi.get(id!),
    enabled: !!id,
  });

  const act = actQuery.data?.act;

  function pdfFilename(): string {
    return `Акт № ${act!.number} от ${formatDateShort(act!.date)}.pdf`;
  }

  async function handleDownload() {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      await downloadActPdf(printRef.current, pdfFilename());
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    if (!act) return;
    const timer = setTimeout(() => {
      if (autoDownload) void handleDownload();
      else window.print();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act, autoDownload]);

  if (!act) {
    return <div style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>Загрузка…</div>;
  }

  const org = act.organization;
  const cp = act.counterparty;

  // «Без НДС» — не то же самое, что 0%: операция не облагается, и в строке
  // «В том числе НДС» печатается «Без налога (НДС)», а не ноль.
  const vatExempt = act.lines.every((l) => l.vatRateValue === null);

  const period =
    act.periodFrom && act.periodTo
      ? `Период оказания услуг: с ${formatDateShort(act.periodFrom)} по ${formatDateShort(act.periodTo)}`
      : null;

  return (
    <div className="act-print" ref={printRef}>
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        .act-print { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; line-height: 1.3; }
        .act-print .act-page {
          box-sizing: border-box;
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          padding: 12mm 10mm;
          background: #fff;
        }

        /* Заголовок «Акт № … от … г.» с жирной чертой во всю ширину — как в бланке. */
        .act-print .title {
          font-size: 15px; font-weight: 700;
          border-bottom: 2px solid #000; padding-bottom: 4px; margin-bottom: 14px;
        }

        /* Реквизиты сторон: подпись слева фиксированной ширины, значение —
           на остаток строки, чтобы «Исполнитель/Заказчик/Основание» стояли
           на одной вертикали. */
        .act-print .party { display: flex; margin-bottom: 9px; }
        .act-print .party .lbl { flex: 0 0 74px; }
        .act-print .party .val { flex: 1 1 auto; font-weight: 700; }

        .act-print .items { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 12px; }
        .act-print .items th, .act-print .items td { border: 1px solid #000; padding: 4px 5px; }
        .act-print .items th { font-weight: 700; text-align: center; vertical-align: middle; }
        .act-print .items td { vertical-align: top; overflow-wrap: anywhere; }
        .act-print .items td.c { text-align: center; }
        .act-print .items td.r { text-align: right; white-space: nowrap; }

        /* Итоги стоят под таблицей справа и без рамки — так в бланке. */
        .act-print .totals { margin-top: 6px; text-align: right; font-weight: 700; }
        .act-print .totals .row { margin-top: 2px; }
        .act-print .totals .val { display: inline-block; min-width: 90px; text-align: right; }

        .act-print .summary { margin-top: 12px; }
        .act-print .words { font-weight: 700; }

        .act-print .clause { margin-top: 12px; padding-bottom: 10px; border-bottom: 2px solid #000; }

        /* Подписи: две колонки одинаковой ширины. Колонки растянуты по высоте
           (align-items: stretch по умолчанию), а линия подписи прижата книзу
           через margin-top: auto — иначе должность исполнителя в две строки
           опускает его линию ниже, чем у заказчика, и подписи оказываются на
           разных уровнях. Отступ сверху задан минимальный, чтобы при короткой
           должности линия не липла к тексту. */
        .act-print .signs { display: flex; gap: 40px; margin-top: 10px; }
        .act-print .sign-col { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; }
        .act-print .sign-role { font-weight: 700; margin-bottom: 2px; }
        .act-print .sign-person { min-height: 30px; }
        .act-print .sign-line { border-bottom: 1px solid #000; margin-top: auto; padding-top: 26px; }
        .act-print .stamp { margin-top: 14px; }

        @media screen {
          .act-print { background: #ddd; padding: 16px 0; }
          .act-print .act-page { box-shadow: 0 0 8px rgba(0,0,0,0.2); }
        }
        @media print {
          .no-print { display: none !important; }
          .act-print { background: #fff; padding: 0; }
          .act-print .act-page { min-height: 0; margin: 0; box-shadow: none; }
        }
      `}</style>

      <div
        className="no-print"
        style={{ textAlign: 'center', marginBottom: 12, display: 'flex', gap: 8, justifyContent: 'center' }}
      >
        <button onClick={() => window.print()} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          Печать
        </button>
        <button
          onClick={() => void handleDownload()}
          disabled={downloading}
          style={{ padding: '6px 14px', cursor: 'pointer' }}
        >
          {downloading ? 'Формирование PDF…' : 'Скачать PDF'}
        </button>
      </div>

      <div className="act-page">
        <div className="title">
          Акт № {act.number} от {formatDateLong(act.date)}
        </div>

        <div className="party">
          <span className="lbl">Исполнитель:</span>
          <span className="val">{partyLine(org)}</span>
        </div>
        <div className="party">
          <span className="lbl">Заказчик:</span>
          <span className="val">{partyLine(cp)}</span>
        </div>
        <div className="party">
          <span className="lbl">Основание:</span>
          <span className="val">{act.basis || '—'}</span>
        </div>
        {period && (
          <div className="party">
            <span className="lbl" />
            <span className="val">{period}</span>
          </div>
        )}

        <table className="items">
          <colgroup>
            <col style={{ width: '6%' }} />
            <col style={{ width: '48%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>№</th>
              <th>Наименование работ, услуг</th>
              <th>Кол-во</th>
              <th>Ед.</th>
              <th>Цена</th>
              <th>Сумма</th>
            </tr>
          </thead>
          <tbody>
            {act.lines.map((line) => (
              <tr key={line.id}>
                <td className="c">{line.lineNumber}</td>
                <td>{line.name}</td>
                <td className="c">{formatQuantity(line.quantity)}</td>
                <td className="c">{line.unitSymbol || '—'}</td>
                <td className="r">{formatMoney(line.price)}</td>
                {/* В графе «Сумма» бланка стоит стоимость с НДС: итог таблицы
                    должен сходиться со строкой «Итого», которую платит заказчик. */}
                <td className="r">{formatMoney(line.totalAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="totals">
          <div className="row">
            Итого: <span className="val">{formatMoney(act.total)}</span>
          </div>
          <div className="row">
            В том числе НДС:{' '}
            <span className="val">{vatExempt ? 'Без налога (НДС)' : formatMoney(act.vatAmount)}</span>
          </div>
        </div>

        <div className="summary">
          <div>
            Всего оказано услуг {act.lines.length} {itemsCountWord(act.lines.length)}, на сумму{' '}
            {formatMoney(act.total)} руб.
          </div>
          <div className="words">{amountInWords(toDecimal(act.total))}</div>
        </div>

        {act.footerText && <div className="clause">{act.footerText}</div>}

        <div className="signs">
          <div className="sign-col">
            <div className="sign-role">ИСПОЛНИТЕЛЬ</div>
            <div className="sign-person">
              {[act.signerPosition, act.signerName].filter(Boolean).join(', ')}
            </div>
            <div className="sign-line" />
            <div className="stamp">М.П.</div>
          </div>
          <div className="sign-col">
            <div className="sign-role">ЗАКАЗЧИК</div>
            <div className="sign-person" />
            <div className="sign-line" />
            <div className="stamp">М.П.</div>
          </div>
        </div>

        {act.comment && <div style={{ marginTop: 12, whiteSpace: 'pre-line' }}>{act.comment}</div>}
      </div>
    </div>
  );
}
